import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { format, addWeeks, addDays, startOfWeek, getWeek, isSameDay, subWeeks } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Plus, FileText, CalendarPlus, Send, Check, ChevronRight,
  Camera, Trash2, Save, X, Clock, Car,
  User, Phone, Mail, ArrowRight, Receipt, Search, Loader2,
  Package, Wrench, Shield, Archive, CheckSquare, ArrowLeft, Calculator,
  ChevronLeft
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import DevisSender from "./DevisSender";

// Category definitions
const CATEGORIES = {
  devis_a_faire: { label: "Devis à faire", icon: FileText, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", type: "predossier" },
  devis_envoye: { label: "Devis envoyés", icon: Send, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200", type: "predossier" },
  a_commander: { label: "À commander", icon: Package, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", type: "vehicle" },
  a_caler: { label: "À caler", icon: CalendarPlus, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", type: "predossier" },
  mecanique: { label: "Mécanique", icon: Wrench, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", type: "vehicle" },
  carrosserie: { label: "Carrosserie", icon: Car, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", type: "vehicle" },
  assurance: { label: "Choc assurance", icon: Shield, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200", type: "vehicle" },
  a_facturer: { label: "À facturer", icon: Receipt, color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-200", type: "vehicle" },
  archives: { label: "Archivés", icon: Archive, color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200", type: "vehicle" },
};

export default function AuBureauView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState({});
  const [activeCategory, setActiveCategory] = useState("devis_a_faire");
  const [categoryItems, setCategoryItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loading, setLoading] = useState(true);

  // Todos
  const [todos, setTodos] = useState([]);
  const [newTodoText, setNewTodoText] = useState("");

  // Pre-dossier form
  const [newDossierDialog, setNewDossierDialog] = useState(false);
  const [devisDialog, setDevisDialog] = useState(false);
  const [convertDialog, setConvertDialog] = useState(false);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Client search
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [showClientResults, setShowClientResults] = useState(false);
  const [searchingClient, setSearchingClient] = useState(false);
  const clientSearchTimeout = useRef(null);

  // Vehicle search
  const [immatSearch, setImmatSearch] = useState("");
  const [vehicleResults, setVehicleResults] = useState([]);
  const [showVehicleResults, setShowVehicleResults] = useState(false);
  const [searchingVehicle, setSearchingVehicle] = useState(false);
  const vehicleSearchTimeout = useRef(null);

  const [formData, setFormData] = useState({
    client: { nom: "", prenom: "", telephone: "", email: "" },
    immatriculation: "", marque: "", modele: "", couleur: "",
    type_intervention: "carrosserie", description: "", montant_devis: "",
    is_assurance: false, photos: []
  });

  const [convertData, setConvertData] = useState({
    date_rdv: "", heure_rdv: "08:00", duree_estimee_heures: 2, duree_estimee_jours: 1
  });
  const [dureeUnite, setDureeUnite] = useState("heures");
  const [convertSmsDialog, setConvertSmsDialog] = useState(false);
  const [convertedVehicleId, setConvertedVehicleId] = useState(null);
  const [sendingSms, setSendingSms] = useState(false);

  // Planning calendar for convert dialog
  const [calendarWeek, setCalendarWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [chargeData, setChargeData] = useState({});

  // Notes (ancien bloc-notes texte)
  const [notes, setNotes] = useState("");
  const [notesEditing, setNotesEditing] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => { fetchOverview(); fetchTodos(); fetchNotes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCategoryItems(); }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (convertDialog && selectedDossier) fetchConvertCharge(); }, [convertDialog, calendarWeek, selectedDossier]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await api.get("/bureau/overview");
      setOverview(res.data || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCategoryItems = async () => {
    setLoadingItems(true);
    try {
      const cat = CATEGORIES[activeCategory];
      if (cat?.type === "predossier") {
        const res = await api.get(`/predossiers?statut=${activeCategory}`);
        setCategoryItems(res.data || []);
      } else if (cat?.type === "vehicle") {
        const res = await api.get(`/bureau/vehicles-by-category/${activeCategory}`);
        setCategoryItems(res.data || []);
      }
    } catch (e) { console.error(e); setCategoryItems([]); }
    setLoadingItems(false);
  };

  const fetchTodos = async () => {
    try {
      const res = await api.get("/bureau/todos");
      setTodos(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchNotes = async () => {
    try {
      const res = await api.get("/bureau/notes");
      setNotes(res.data?.content || "");
    } catch (e) { console.error(e); }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put("/bureau/notes", { content: notes });
      toast.success("Notes sauvegardées");
      setNotesEditing(false);
    } catch (e) { toast.error("Erreur"); }
    setSavingNotes(false);
  };

  const fetchConvertCharge = async () => {
    try {
      const startDate = format(calendarWeek, "yyyy-MM-dd");
      const endDate = format(addDays(addWeeks(calendarWeek, 3), -1), "yyyy-MM-dd");
      const res = await api.get(`/planning/charge?start_date=${startDate}&end_date=${endDate}`);
      setChargeData(res.data || {});
    } catch (e) { console.error(e); }
  };

  const getCalendarDays = () => {
    const days = [];
    for (let w = 0; w < 3; w++) {
      for (let d = 0; d < 5; d++) {
        days.push(addDays(addWeeks(calendarWeek, w), d));
      }
    }
    return days;
  };

  // --- Todo handlers ---
  const addTodo = async () => {
    if (!newTodoText.trim()) return;
    try {
      await api.post("/bureau/todos", { text: newTodoText.trim() });
      setNewTodoText("");
      fetchTodos();
    } catch (e) { toast.error("Erreur"); }
  };

  const toggleTodo = async (todo) => {
    try {
      await api.put(`/bureau/todos/${todo.id}`, { checked: !todo.checked });
      fetchTodos();
    } catch (e) { toast.error("Erreur"); }
  };

  const deleteTodo = async (todoId) => {
    try {
      await api.delete(`/bureau/todos/${todoId}`);
      fetchTodos();
    } catch (e) { toast.error("Erreur"); }
  };

  // --- Pre-dossier handlers ---
  const resetForm = () => {
    setFormData({ client: { nom: "", prenom: "", telephone: "", email: "" },
      immatriculation: "", marque: "", modele: "", couleur: "",
      type_intervention: "carrosserie", description: "", montant_devis: "",
      is_assurance: false, photos: [] });
    setClientSearch(""); setImmatSearch("");
    setClientResults([]); setVehicleResults([]);
  };

  const searchClients = async (query) => {
    if (query.length < 2) { setClientResults([]); setShowClientResults(false); return; }
    setSearchingClient(true);
    try { const res = await api.get(`/clients?search=${encodeURIComponent(query)}`); setClientResults(res.data || []); setShowClientResults(true); }
    catch (e) { console.error(e); }
    setSearchingClient(false);
  };

  const handleClientSearchChange = (value) => {
    setClientSearch(value);
    setFormData(prev => ({ ...prev, client: { ...prev.client, nom: value } }));
    if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);
    clientSearchTimeout.current = setTimeout(() => searchClients(value), 300);
  };

  const selectClient = (client) => {
    setFormData(prev => ({ ...prev, client: { nom: client.nom || "", prenom: client.prenom || "", telephone: client.telephone || "", email: client.email || "" } }));
    setClientSearch(`${client.nom} ${client.prenom || ""}`);
    setShowClientResults(false);
  };

  const searchVehicles = async (query) => {
    if (query.length < 2) { setVehicleResults([]); setShowVehicleResults(false); return; }
    setSearchingVehicle(true);
    try { const res = await api.get(`/vehicles?search=${encodeURIComponent(query)}`); setVehicleResults((res.data || []).slice(0, 10)); setShowVehicleResults(true); }
    catch (e) { console.error(e); }
    setSearchingVehicle(false);
  };

  const handleImmatSearchChange = (value) => {
    const upper = value.toUpperCase();
    setImmatSearch(upper);
    setFormData(prev => ({ ...prev, immatriculation: upper }));
    if (vehicleSearchTimeout.current) clearTimeout(vehicleSearchTimeout.current);
    vehicleSearchTimeout.current = setTimeout(() => searchVehicles(upper), 300);
  };

  const selectVehicle = (vehicle) => {
    setFormData(prev => ({
      ...prev, immatriculation: vehicle.immatriculation || "", marque: vehicle.marque || "", modele: vehicle.modele || "", couleur: vehicle.couleur || "",
      ...(vehicle.client ? { client: { nom: vehicle.client.nom || prev.client.nom, prenom: vehicle.client.prenom || prev.client.prenom, telephone: vehicle.client.telephone || prev.client.telephone, email: vehicle.client.email || prev.client.email } } : {})
    }));
    setImmatSearch(vehicle.immatriculation);
    if (vehicle.client) setClientSearch(`${vehicle.client.nom} ${vehicle.client.prenom || ""}`);
    setShowVehicleResults(false);
  };

  const handleCreateDossier = async () => {
    if (!formData.client.nom || !formData.immatriculation || !formData.marque) {
      toast.error("Remplissez les champs obligatoires"); return;
    }
    setProcessing(true);
    try {
      await api.post("/predossiers", { ...formData, montant_devis: formData.montant_devis ? parseFloat(formData.montant_devis) : null });
      toast.success("Pré-dossier créé");
      setNewDossierDialog(false); resetForm();
      fetchOverview(); fetchCategoryItems();
    } catch (e) { toast.error("Erreur création"); }
    setProcessing(false);
  };

  const handleStatusChange = async (dossier, newStatus) => {
    try {
      await api.put(`/predossiers/${dossier.id}`, { statut: newStatus });
      toast.success("Statut mis à jour");
      fetchOverview(); fetchCategoryItems();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleConvert = async () => {
    if (!convertData.date_rdv) { toast.error("Sélectionnez une date"); return; }
    setProcessing(true);
    try {
      let payload;
      if (dureeUnite === "jours") {
        const j = convertData.duree_estimee_jours || 1;
        payload = { ...convertData, duree_estimee_jours: j, duree_estimee_heures: j * 8 };
      } else {
        const h = convertData.duree_estimee_heures || 2;
        payload = { ...convertData, duree_estimee_heures: h, duree_estimee_jours: Math.ceil(h / 8) || 1 };
      }
      if (selectedDossier?.is_assurance) payload.is_assurance = true;
      const res = await api.post(`/predossiers/${selectedDossier.id}/convert`, payload);
      toast.success("Dossier envoyé au planning !");
      setConvertDialog(false);
      fetchOverview(); fetchCategoryItems();
      if (res.data?.vehicle_id) {
        setConvertedVehicleId(res.data.vehicle_id);
        setConvertSmsDialog(true);
      } else {
        setSelectedDossier(null);
      }
    } catch (e) { toast.error("Erreur conversion"); }
    setProcessing(false);
  };

  const handleDelete = async (dossierId) => {
    if (!confirm("Supprimer ce pré-dossier ?")) return;
    try {
      await api.delete(`/predossiers/${dossierId}`);
      toast.success("Supprimé"); fetchOverview(); fetchCategoryItems();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleMarkInvoiced = async (vehicleId) => {
    try {
      await api.put(`/vehicles/${vehicleId}`, { facture_envoyee: true });
      toast.success("Marqué comme facturé");
      fetchOverview(); fetchCategoryItems();
    } catch (e) { toast.error("Erreur"); }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData(prev => ({ ...prev, photos: [...prev.photos, reader.result] }));
      reader.readAsDataURL(file);
    }
  };

  const catDef = CATEGORIES[activeCategory];
  const CatIcon = catDef?.icon || FileText;

  // --- Render category items ---
  const renderItem = (item) => {
    const isPredossier = catDef?.type === "predossier";

    if (isPredossier) {
      return (
        <Card key={item.id} className="hover:border-slate-400 transition-colors">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-slate-900 text-sm">{item.immatriculation}</span>
                  {item.montant_devis && <Badge variant="outline" className="text-emerald-600 text-xs">{item.montant_devis}€</Badge>}
                  {item.is_assurance && <Badge className="bg-indigo-100 text-indigo-700 text-xs">Assurance</Badge>}
                </div>
                <p className="text-xs text-slate-600 truncate">{item.marque} {item.modele}</p>
                <p className="text-xs text-slate-500">{item.client?.nom} {item.client?.prenom} {item.client?.telephone ? `• ${item.client.telephone}` : ""}</p>
                {item.description && <p className="text-xs text-slate-400 truncate mt-0.5">{item.description}</p>}
                {item.note && <p className="text-xs text-amber-700 truncate mt-0.5">Note : {item.note}</p>}
                {item.devis_link && (
                  <a href={item.devis_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-blue-600 underline inline-block mt-0.5" data-testid="predossier-devis-link">
                    Voir le devis
                  </a>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {activeCategory === "devis_a_faire" && (
                  <Button size="sm" variant="outline" className="text-purple-600 border-purple-300 text-xs h-7 px-2"
                    onClick={() => handleStatusChange(item, "devis_envoye")}>
                    <Send className="h-3 w-3 mr-1" />Envoyé
                  </Button>
                )}
                {activeCategory === "devis_envoye" && (
                  <Button size="sm" variant="outline" className="text-orange-600 border-orange-300 text-xs h-7 px-2"
                    onClick={() => handleStatusChange(item, "a_caler")}>
                    <Check className="h-3 w-3 mr-1" />OK
                  </Button>
                )}
                {activeCategory === "a_caler" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7 px-2"
                    onClick={() => {
                      setSelectedDossier(item);
                      const isMeca = item.type_intervention === "mecanique";
                      setDureeUnite(isMeca ? "heures" : "jours");
                      setConvertData({ date_rdv: "", heure_rdv: "08:00", duree_estimee_heures: 2, duree_estimee_jours: 1 });
                      setConvertDialog(true);
                    }}>
                    <CalendarPlus className="h-3 w-3 mr-1" />Planifier
                  </Button>
                )}
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDelete(item.id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Vehicle item
    return (
      <Card key={item.id} className={`hover:border-slate-400 transition-colors cursor-pointer ${item.is_assurance ? "border-indigo-200" : ""}`}
        onClick={() => navigate(`/vehicles/${item.id}`)}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-slate-900 text-sm">{item.immatriculation}</span>
                {item.is_assurance && <Badge className="bg-indigo-100 text-indigo-700 text-xs">Assurance</Badge>}
                {item.priorite && <div className="w-2.5 h-2.5 rounded-full bg-red-500" title="Prioritaire" />}
                {activeCategory === "a_commander" && <Badge className="bg-red-100 text-red-700 text-xs"><Package className="h-3 w-3 mr-1" />Pièces manquantes</Badge>}
                {item.montant_facture && <Badge variant="outline" className="text-emerald-600 text-xs">{item.montant_facture}€</Badge>}
              </div>
              <p className="text-xs text-slate-600 truncate">{item.marque} {item.modele}</p>
              <p className="text-xs text-slate-500">{item.client?.nom} {item.client?.prenom}</p>
              {item.description && <p className="text-xs text-slate-400 truncate mt-0.5">{item.description}</p>}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {activeCategory === "a_facturer" && (
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-xs h-7 px-2"
                  onClick={() => handleMarkInvoiced(item.id)}>
                  <Check className="h-3 w-3 mr-1" />Facturé
                </Button>
              )}
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="au-bureau-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Au Bureau</h1>
          <p className="text-sm text-slate-500">Gestion des dossiers clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setDevisDialog(true)} data-testid="open-devis-btn">
            <Send className="h-4 w-4 mr-2" />Envoyer un devis
          </Button>
          <Button onClick={() => { resetForm(); setNewDossierDialog(true); }} className="bg-red-600 hover:bg-red-700" data-testid="new-predossier-btn">
            <Plus className="h-4 w-4 mr-2" />Nouveau pré-dossier
          </Button>
        </div>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          const count = overview[key] || 0;
          const isActive = activeCategory === key;
          return (
            <button key={key} onClick={() => setActiveCategory(key)}
              className={`p-2.5 rounded-lg border-2 transition-all text-center ${isActive ? `${cat.border} ${cat.bg} ring-2 ring-offset-1 ring-current` : "border-slate-200 hover:border-slate-300 bg-white"}`}
              data-testid={`cat-${key}`}>
              <Icon className={`h-5 w-5 mx-auto mb-1 ${isActive ? cat.color : "text-slate-400"}`} />
              <p className={`text-lg font-bold ${isActive ? cat.color : "text-slate-700"}`}>{count}</p>
              <p className="text-[10px] text-slate-500 leading-tight">{cat.label}</p>
            </button>
          );
        })}
      </div>

      {/* Active Category Content */}
      <Card className={`${catDef?.border || "border-slate-200"}`}>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base flex items-center gap-2">
            <CatIcon className={`h-5 w-5 ${catDef?.color}`} />
            {catDef?.label} ({categoryItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {loadingItems ? (
            <div className="flex justify-center py-6"><div className="spinner" /></div>
          ) : categoryItems.length === 0 ? (
            <div className="text-center py-6 text-slate-400">
              <CatIcon className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun dossier</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {categoryItems.map(renderItem)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bloc-notes à cocher */}
      <Card className="border-amber-200">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-amber-600" />
            Bloc-notes partagé
            <span className="text-xs font-normal text-slate-400 ml-2">Auto-suppression 7j après validation</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {/* Add new todo */}
          <div className="flex gap-2 mb-3">
            <Input placeholder="Nouvelle action..." value={newTodoText}
              onChange={(e) => setNewTodoText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTodo()}
              className="text-sm" data-testid="new-todo-input" />
            <Button size="sm" onClick={addTodo} className="bg-amber-600 hover:bg-amber-700 px-3" data-testid="add-todo-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Todo list */}
          <div className="space-y-1">
            {todos.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-3">Aucune action en cours</p>
            ) : (
              todos.map((todo) => (
                <div key={todo.id} className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${todo.checked ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}
                  data-testid={`todo-${todo.id}`}>
                  <Checkbox checked={todo.checked} onCheckedChange={() => toggleTodo(todo)}
                    className="data-[state=checked]:bg-emerald-600" />
                  <span className={`flex-1 text-sm ${todo.checked ? "line-through text-slate-400" : "text-slate-700"}`}>
                    {todo.text}
                  </span>
                  {todo.checked && todo.checked_by && (
                    <span className="text-xs text-emerald-600 flex-shrink-0">
                      {todo.checked_by}
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400 flex-shrink-0">{todo.created_by}</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-red-500 flex-shrink-0"
                    onClick={() => deleteTodo(todo.id)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notes texte (ancien bloc-notes) */}
      {notes && !notesEditing && (
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-500" />
                Notes
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setNotesEditing(true)}>Modifier</Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="p-3 bg-slate-50 rounded-lg border">
              <p className="whitespace-pre-wrap text-sm text-slate-700">{notes}</p>
            </div>
          </CardContent>
        </Card>
      )}
      {notesEditing && (
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-500" />
                Notes
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setNotesEditing(false); fetchNotes(); }}>
                  <X className="h-4 w-4 mr-1" />Annuler
                </Button>
                <Button size="sm" onClick={handleSaveNotes} disabled={savingNotes} className="bg-emerald-600 hover:bg-emerald-700">
                  <Save className="h-4 w-4 mr-1" />{savingNotes ? "..." : "Sauvegarder"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes libres..." className="min-h-[100px]" />
          </CardContent>
        </Card>
      )}


      {/* Dialog nouveau pré-dossier */}
      <Dialog open={newDossierDialog} onOpenChange={(open) => { setNewDossierDialog(open); if (!open) { setShowClientResults(false); setShowVehicleResults(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau pré-dossier</DialogTitle>
            <DialogDescription>Créer un dossier pour un nouveau client / devis</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Client */}
            <div className="space-y-3">
              <h3 className="font-medium flex items-center gap-2 text-sm"><User className="h-4 w-4" />Client</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Label className="text-xs">Nom * <Search className="h-3 w-3 inline text-slate-400" /></Label>
                  <div className="relative">
                    <Input value={clientSearch || formData.client.nom} onChange={(e) => handleClientSearchChange(e.target.value)}
                      onFocus={() => clientResults.length > 0 && setShowClientResults(true)} placeholder="Rechercher..." className="pr-8 text-sm" />
                    {searchingClient && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
                  </div>
                  {showClientResults && clientResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {clientResults.map((c) => (
                        <div key={c.id} className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0" onClick={() => selectClient(c)}>
                          <p className="font-medium text-sm">{c.nom} {c.prenom}</p>
                          <p className="text-xs text-slate-500">{c.telephone}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Prénom</Label>
                  <Input value={formData.client.prenom} onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, prenom: e.target.value } }))} className="text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" />Téléphone</Label>
                  <Input value={formData.client.telephone} onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, telephone: e.target.value } }))} className="text-sm" />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" />Email</Label>
                  <Input value={formData.client.email} onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, email: e.target.value } }))} className="text-sm" />
                </div>
              </div>
            </div>

            {/* Véhicule */}
            <div className="space-y-3">
              <h3 className="font-medium flex items-center gap-2 text-sm"><Car className="h-4 w-4" />Véhicule</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="relative">
                  <Label className="text-xs">Immatriculation * <Search className="h-3 w-3 inline text-slate-400" /></Label>
                  <div className="relative">
                    <Input value={immatSearch || formData.immatriculation} onChange={(e) => handleImmatSearchChange(e.target.value)}
                      onFocus={() => vehicleResults.length > 0 && setShowVehicleResults(true)} placeholder="AA-123-BB" className="pr-8 uppercase text-sm" />
                    {searchingVehicle && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
                  </div>
                  {showVehicleResults && vehicleResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {vehicleResults.map((v, i) => (
                        <div key={v.id || i} className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0" onClick={() => selectVehicle(v)}>
                          <p className="font-mono font-bold text-sm">{v.immatriculation}</p>
                          <p className="text-xs text-slate-500">{v.marque} {v.modele}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Marque *</Label>
                  <Input value={formData.marque} onChange={(e) => setFormData(prev => ({ ...prev, marque: e.target.value }))} className="text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Modèle</Label>
                  <Input value={formData.modele} onChange={(e) => setFormData(prev => ({ ...prev, modele: e.target.value }))} className="text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={formData.type_intervention} onValueChange={(v) => setFormData(prev => ({ ...prev, type_intervention: v }))}>
                    <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="carrosserie">Carrosserie</SelectItem>
                      <SelectItem value="mecanique">Mécanique</SelectItem>
                      <SelectItem value="peinture">Peinture divers</SelectItem>
                      <SelectItem value="pare_brise">Pare-brise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Montant devis (€)</Label>
                  <Input type="number" value={formData.montant_devis} onChange={(e) => setFormData(prev => ({ ...prev, montant_devis: e.target.value }))} className="text-sm" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={formData.is_assurance} onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_assurance: v }))} />
                    <span className="text-sm font-medium flex items-center gap-1"><Shield className="h-3.5 w-3.5 text-indigo-600" />Assurance</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs">Description des travaux</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="mt-1 text-sm" />
            </div>

            {/* Photos */}
            <div>
              <Label className="text-xs">Photos</Label>
              <div className="flex gap-2 mt-1">
                <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} onChange={handlePhotoCapture} className="hidden" />
                <input type="file" accept="image/*" ref={galleryInputRef} onChange={handlePhotoCapture} className="hidden" />
                <Button type="button" variant="outline" size="sm" onClick={() => cameraInputRef.current?.click()}>
                  <Camera className="h-4 w-4 mr-1" />Photo
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => galleryInputRef.current?.click()}>Galerie</Button>
              </div>
              {formData.photos.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {formData.photos.map((photo, idx) => (
                    <div key={`${idx}-${(photo || '').slice(-16)}`} className="relative">
                      <img src={photo} alt="" className="w-16 h-16 object-cover rounded-lg" />
                      <button className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                        onClick={() => setFormData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }))}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDossierDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateDossier} disabled={processing} className="bg-red-600 hover:bg-red-700">
              {processing ? <div className="spinner" /> : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog planifier (convertir en RDV) avec calendrier charge */}
      <Dialog open={convertDialog} onOpenChange={setConvertDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Planifier le RDV</DialogTitle>
            <DialogDescription>
              {selectedDossier?.immatriculation} — {selectedDossier?.marque} {selectedDossier?.modele}
              <Badge className="ml-2 text-xs" variant="outline">
                {selectedDossier?.type_intervention === "mecanique" ? "Mécanique" : "Carrosserie"}
              </Badge>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Mini Planning */}
            <div className="border rounded-lg p-2 max-h-[400px] overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-700">Planning</span>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
                    onClick={() => setCalendarWeek(subWeeks(calendarWeek, 1))}>
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <span className="text-xs font-bold text-slate-700 min-w-[30px] text-center">
                    S{getWeek(calendarWeek, { weekStartsOn: 1 })}
                  </span>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
                    onClick={() => setCalendarWeek(addWeeks(calendarWeek, 1))}>
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Suggestion créneau */}
              {(() => {
                const isMeca = selectedDossier?.type_intervention === "mecanique";
                const today = new Date(); today.setHours(0,0,0,0);
                let bestDate = null; let bestCount = 999;
                for (let w = 0; w < 3; w++) {
                  for (let d = 0; d < 5; d++) {
                    const day = addDays(calendarWeek, w * 7 + d);
                    if (day < today) continue;
                    const ds = format(day, "yyyy-MM-dd");
                    const dc = chargeData[ds];
                    if (!dc) continue;
                    const c = isMeca ? (dc.meca_vehicles?.length || 0) : (dc.carro_vehicles?.length || 0);
                    if (c < bestCount) { bestCount = c; bestDate = ds; }
                  }
                }
                if (bestDate && bestDate !== convertData.date_rdv) {
                  return (
                    <button type="button" onClick={() => setConvertData(prev => ({ ...prev, date_rdv: bestDate }))}
                      className="w-full mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-left hover:bg-blue-100 transition-all">
                      <p className="text-[10px] font-bold text-blue-700">Créneau suggéré</p>
                      <p className="text-xs text-blue-600">
                        {format(new Date(bestDate), "EEEE d MMMM", { locale: fr })} — {bestCount} {isMeca ? "méca" : "carro"} ce jour
                      </p>
                    </button>
                  );
                }
                return null;
              })()}

              {[0, 1, 2].map((weekIdx) => {
                const wo = weekIdx * 7;
                return (
                  <div key={weekIdx} className={weekIdx > 0 ? "mt-1.5 pt-1.5 border-t border-dashed border-slate-300" : ""}>
                    <div className="grid grid-cols-5 gap-0.5 mb-0.5">
                      {[0,1,2,3,4].map((d) => {
                        const day = addDays(calendarWeek, wo + d);
                        const dateStr = format(day, "yyyy-MM-dd");
                        const isSelected = convertData.date_rdv === dateStr;
                        const isToday = isSameDay(day, new Date());
                        return (
                          <button key={d} type="button" onClick={() => setConvertData(prev => ({ ...prev, date_rdv: dateStr }))}
                            className={`text-center py-0.5 rounded-t text-[9px] font-bold ${isSelected ? "bg-red-600 text-white" : isToday ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                            {format(day, "EEE d", { locale: fr })}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mb-0.5">
                      <div className="text-[8px] font-bold text-emerald-700 bg-emerald-50 px-1 rounded-t">MÉCA</div>
                      <div className="grid grid-cols-5 gap-px bg-emerald-50/30 rounded-b min-h-[30px]">
                        {[0,1,2,3,4].map((d) => {
                          const day = addDays(calendarWeek, wo + d);
                          const dateStr = format(day, "yyyy-MM-dd");
                          const dc = chargeData[dateStr] || { meca_vehicles: [] };
                          return (
                            <div key={d} onClick={() => setConvertData(prev => ({ ...prev, date_rdv: dateStr }))}
                              className={`cursor-pointer p-0.5 space-y-0.5 min-h-[25px] border-r last:border-r-0 border-emerald-100 ${convertData.date_rdv === dateStr ? "bg-red-50 ring-1 ring-red-400 rounded" : "hover:bg-emerald-50"}`}>
                              {dc.meca_vehicles?.length === 0 ? <span className="text-[7px] text-slate-300 flex items-center justify-center h-full">—</span>
                              : dc.meca_vehicles?.map((v, vi) => (
                                <div key={vi} className="bg-emerald-500 text-white text-[6px] px-0.5 rounded truncate leading-tight" title={v.immatriculation + (v.description ? ' - ' + v.description : '')}>
                                  {v.client_nom || v.immatriculation}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[8px] font-bold text-orange-700 bg-orange-50 px-1 rounded-t">CARRO</div>
                      <div className="grid grid-cols-5 gap-px bg-orange-50/30 rounded-b min-h-[30px]">
                        {[0,1,2,3,4].map((d) => {
                          const day = addDays(calendarWeek, wo + d);
                          const dateStr = format(day, "yyyy-MM-dd");
                          const dc = chargeData[dateStr] || { carro_vehicles: [] };
                          return (
                            <div key={d} onClick={() => setConvertData(prev => ({ ...prev, date_rdv: dateStr }))}
                              className={`cursor-pointer p-0.5 space-y-0.5 min-h-[25px] border-r last:border-r-0 border-orange-100 ${convertData.date_rdv === dateStr ? "bg-red-50 ring-1 ring-red-400 rounded" : "hover:bg-orange-50"}`}>
                              {dc.carro_vehicles?.length === 0 ? <span className="text-[7px] text-slate-300 flex items-center justify-center h-full">—</span>
                              : dc.carro_vehicles?.map((v, vi) => (
                                <div key={vi} className="bg-orange-500 text-white text-[6px] px-0.5 rounded truncate leading-tight" title={v.immatriculation + (v.description ? ' - ' + v.description : '')}>
                                  {v.client_nom || v.immatriculation}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Heure</Label>
                <Select value={convertData.heure_rdv} onValueChange={(v) => setConvertData(prev => ({ ...prev, heure_rdv: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["08:00","09:00","10:00","11:00","14:00","15:00","16:00"].map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Durée estimée</Label>
                <div className="flex gap-1 mt-1">
                  <div className="flex rounded-md border overflow-hidden shrink-0">
                    <button type="button" onClick={() => setDureeUnite("heures")} className={`px-2 text-xs ${dureeUnite === "heures" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="duree-unite-heures">Heures</button>
                    <button type="button" onClick={() => setDureeUnite("jours")} className={`px-2 text-xs ${dureeUnite === "jours" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="duree-unite-jours">Jours</button>
                  </div>
                  {dureeUnite === "jours" ? (
                    <Input type="number" min="1" step="1" value={convertData.duree_estimee_jours}
                      onChange={(e) => setConvertData(prev => ({ ...prev, duree_estimee_jours: parseInt(e.target.value) || 1 }))} className="flex-1" data-testid="convert-duree-jours" />
                  ) : (
                    <Input type="number" min="0.5" step="0.5" value={convertData.duree_estimee_heures}
                      onChange={(e) => setConvertData(prev => ({ ...prev, duree_estimee_heures: parseFloat(e.target.value) || 0.5 }))} className="flex-1" data-testid="convert-duree-heures" />
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialog(false)}>Annuler</Button>
            <Button onClick={handleConvert} disabled={processing || !convertData.date_rdv} className="bg-emerald-600 hover:bg-emerald-700">
              {processing ? <div className="spinner" /> : <><ArrowRight className="h-4 w-4 mr-2" />Envoyer au planning</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proposition SMS de confirmation après planification */}
      <Dialog open={convertSmsDialog} onOpenChange={(open) => { if (!open) { setConvertSmsDialog(false); setSelectedDossier(null); } }}>
        <DialogContent className="max-w-md" data-testid="convert-sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-blue-600" />Envoyer un SMS de confirmation ?</DialogTitle>
            <DialogDescription>Voulez-vous envoyer un SMS de confirmation au client pour ce nouveau RDV ?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={sendingSms} onClick={() => { setConvertSmsDialog(false); setSelectedDossier(null); }} data-testid="convert-sms-no">Non, pas maintenant</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={sendingSms} data-testid="convert-sms-yes"
              onClick={async () => {
                setSendingSms(true);
                try {
                  await api.post(`/vehicles/${convertedVehicleId}/send-confirmation-sms`);
                  toast.success("SMS de confirmation envoyé");
                } catch (e) { toast.error("Erreur envoi SMS"); }
                setSendingSms(false);
                setConvertSmsDialog(false);
                setSelectedDossier(null);
              }}>
              {sendingSms ? <div className="spinner" /> : "Oui, envoyer le SMS"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog envoyer un devis */}
      <Dialog open={devisDialog} onOpenChange={setDevisDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Envoyer un devis par SMS</DialogTitle>
            <DialogDescription>Envoyez un lien de devis à un client existant ou nouveau</DialogDescription>
          </DialogHeader>
          <DevisSender onSent={() => setDevisDialog(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
